import { useEffect, useState, useRef } from 'react';
import {
  X,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Share2,
  Download,
  Info,
  SlidersHorizontal,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  buildImageCssFilter,
  DEFAULT_IMAGE_FILTER_SETTINGS,
  type Friend,
  type ImageFilterSettings,
} from '@omokage/shared';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { Photo } from '@/types/photo';
import { useUploadPhoto } from '@/hooks/usePhotos';
import { useSwipe } from '@/hooks/useSwipe';
import { useCreateShare, copyShareUrl } from '@/hooks/useShares';
import { usePhotoDownload } from '@/hooks/useDownload';
import { useUserPlan, getDownloadQualityLabel } from '@/hooks/usePlan';
import { usePresence } from '@/hooks/usePresence';
import {
  buildFilteredUploadFilename,
  buildPhotoUploadMetadata,
  fetchImageBlob,
  renderFilteredImageBlob,
} from '@/lib/imageFilters';
import { ImageFilterPanel } from './ImageFilterPanel';
import { PhotoMetadataPanel } from './PhotoMetadataPanel';

/** Minimum swipe distance in pixels to trigger navigation */
const SWIPE_THRESHOLD = 50;

export interface PhotoModalProps {
  photo: Photo;
  photos?: Photo[];
  isOpen: boolean;
  onClose: () => void;
  onNavigate?: (photo: Photo) => void;
  onDelete?: (photo: Photo) => void;
  showMetadata?: boolean;
  onPlayerRemoved?: (vrchatId: string) => void;
  onPlayerAdded?: (player: Friend) => void;
  readOnly?: boolean;
}

export function PhotoModal({
  photo,
  photos,
  isOpen,
  onClose,
  onNavigate,
  onDelete,
  showMetadata = false,
  onPlayerRemoved,
  onPlayerAdded,
  readOnly = false,
}: PhotoModalProps) {
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomScale, setZoomScale] = useState(1);
  const [imageError, setImageError] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // Mobile devices (< 640px) default to closed, desktop defaults to open
  const [showMetadataPanel, setShowMetadataPanel] = useState(
    () => typeof window !== 'undefined' && window.innerWidth >= 640
  );
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [showCropOverlay, setShowCropOverlay] = useState(false);
  const [filterSettings, setFilterSettings] = useState<ImageFilterSettings>(
    DEFAULT_IMAGE_FILTER_SETTINGS
  );
  const [isSavingFiltered, setIsSavingFiltered] = useState(false);
  // Animation states
  const [isImageTransitioning, setIsImageTransitioning] = useState(false);
  const previousPhotoId = useRef<string | null>(null);
  // Pinch zoom state
  const initialDistance = useRef<number | null>(null);
  const initialScale = useRef<number>(1);

  // Share functionality
  const createShare = useCreateShare();

  // Download functionality
  const { downloadPhoto, isDownloading } = usePhotoDownload();
  const uploadPhoto = useUploadPhoto();

  // User plan for download quality label
  const { data: userPlan } = useUserPlan();

  // Get current photo index (use optional chaining for null safety)
  const currentIndex = photos?.findIndex((p) => p.id === photo?.id) ?? -1;
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < (photos?.length ?? 0) - 1;

  // Handle image switching animation
  useEffect(() => {
    if (!photo) return;
    if (previousPhotoId.current && previousPhotoId.current !== photo.id) {
      // Photo changed, trigger transition
      setIsImageTransitioning(true);
      const timer = setTimeout(() => {
        setIsImageTransitioning(false);
      }, 200);
      return () => clearTimeout(timer);
    }
    previousPhotoId.current = photo.id;
  }, [photo]);

  useEffect(() => {
    setShowFilterPanel(false);
    setShowCropOverlay(false);
    setFilterSettings(DEFAULT_IMAGE_FILTER_SETTINGS);
  }, [photo.id]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          if (hasPrev && photos && onNavigate) {
            const prevPhoto = photos[currentIndex - 1];
            if (prevPhoto) {
              onNavigate(prevPhoto);
              setIsZoomed(false);
            }
          }
          break;
        case 'ArrowRight':
          if (hasNext && photos && onNavigate) {
            const nextPhoto = photos[currentIndex + 1];
            if (nextPhoto) {
              onNavigate(nextPhoto);
              setIsZoomed(false);
            }
          }
          break;
        case 'i':
        case 'I':
          setShowFilterPanel(false);
          setShowMetadataPanel((prev) => !prev);
          break;
        case 'f':
        case 'F':
          if (readOnly) break;
          setShowFilterPanel((prev) => {
            const next = !prev;
            if (next) {
              setShowMetadataPanel(false);
            }
            return next;
          });
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, hasPrev, hasNext, photos, currentIndex, onNavigate, readOnly]);

  // Reset zoom, error, and delete confirm state when photo changes
  useEffect(() => {
    setIsZoomed(false);
    setZoomScale(1);
    setImageError(false);
    setShowDeleteConfirm(false);
  }, [photo?.id]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handler functions
  const handlePrevious = () => {
    if (hasPrev && photos && onNavigate) {
      const prevPhoto = photos[currentIndex - 1];
      if (prevPhoto) {
        onNavigate(prevPhoto);
      }
    }
  };

  const handleNext = () => {
    if (hasNext && photos && onNavigate) {
      const nextPhoto = photos[currentIndex + 1];
      if (nextPhoto) {
        onNavigate(nextPhoto);
      }
    }
  };

  const swipeHandlers = useSwipe(
    {
      onSwipedLeft: () => {
        if (!isZoomed && hasNext) {
          handleNext();
        }
      },
      onSwipedRight: () => {
        if (!isZoomed && hasPrev) {
          handlePrevious();
        }
      },
      onSwipedDown: () => {
        if (!isZoomed) {
          onClose();
        }
      },
    },
    {
      delta: SWIPE_THRESHOLD,
    }
  );

  const modalPresence = usePresence(isOpen, { duration: 200 });
  const deleteConfirmPresence = usePresence(showDeleteConfirm, { duration: 200 });
  const metadataPanelPresence = usePresence(showMetadataPanel, { duration: 300 });
  const filterPanelPresence = usePresence(showFilterPanel, { duration: 200 });

  // Early returns after all hooks
  if (!photo) return null;
  if (!modalPresence.isMounted) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleImageDoubleClick = () => {
    if (isZoomed) {
      setIsZoomed(false);
      setZoomScale(1);
    } else {
      setIsZoomed(true);
      setZoomScale(2);
    }
  };

  // Pinch zoom handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    swipeHandlers.onTouchStart(e);

    if (e.touches.length === 2) {
      // Pinch gesture started
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      if (touch1 && touch2) {
        const distance = Math.hypot(
          touch2.clientX - touch1.clientX,
          touch2.clientY - touch1.clientY
        );
        initialDistance.current = distance;
        initialScale.current = zoomScale;
      }
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    swipeHandlers.onTouchMove(e);

    if (e.touches.length === 2 && initialDistance.current !== null) {
      // Pinch zoom in progress
      e.preventDefault();
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      if (touch1 && touch2) {
        const distance = Math.hypot(
          touch2.clientX - touch1.clientX,
          touch2.clientY - touch1.clientY
        );
        const scale = (distance / initialDistance.current) * initialScale.current;
        // Clamp scale between 1 and 4
        const clampedScale = Math.max(1, Math.min(4, scale));
        setZoomScale(clampedScale);
        setIsZoomed(clampedScale > 1);
      }
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    swipeHandlers.onTouchEnd(e);

    if (e.touches.length < 2) {
      // Pinch gesture ended
      initialDistance.current = null;
      // Snap to 1x if close to it
      if (zoomScale < 1.2) {
        setZoomScale(1);
        setIsZoomed(false);
      }
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    onDelete?.(photo);
    setShowDeleteConfirm(false);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  const handleShare = async () => {
    try {
      const share = await createShare.mutateAsync({ photoId: photo.id });
      // Use shareUrl from API response (already contains correct backend URL)
      const copied = await copyShareUrl(share.shareUrl, true);

      if (copied) {
        toast.success('リンクをコピーしました');
      } else {
        toast.error('クリップボードへのコピーに失敗しました');
      }
    } catch {
      toast.error('共有リンクの作成に失敗しました');
    }
  };

  const handleDownload = async () => {
    try {
      await downloadPhoto(photo.id, photo.originalFilename || undefined);
    } catch {
      // Error already handled in usePhotoDownload
    }
  };

  const handleSaveFiltered = async () => {
    try {
      setIsSavingFiltered(true);
      const originalBlob = await fetchImageBlob(photo.originalUrl);
      const filteredBlob = await renderFilteredImageBlob(originalBlob, filterSettings);
      const filteredFile = new File([filteredBlob], buildFilteredUploadFilename(photo), {
        type: filteredBlob.type || originalBlob.type || 'image/jpeg',
      });

      await uploadPhoto.mutateAsync({
        file: filteredFile,
        metadata: buildPhotoUploadMetadata(photo),
      });

      toast.success('加工画像を新しい写真として保存しました');
      setShowFilterPanel(false);
      setFilterSettings(DEFAULT_IMAGE_FILTER_SETTINGS);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '加工画像の保存に失敗しました');
    } finally {
      setIsSavingFiltered(false);
    }
  };

  const handleDownloadFiltered = async () => {
    try {
      setIsSavingFiltered(true);
      const originalBlob = await fetchImageBlob(photo.originalUrl);
      const filteredBlob = await renderFilteredImageBlob(originalBlob, filterSettings);
      const filename = buildFilteredUploadFilename(photo);
      const url = URL.createObjectURL(filteredBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '加工画像のダウンロードに失敗しました');
    } finally {
      setIsSavingFiltered(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      data-presence={modalPresence.phase}
      className={cn(
        'fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-md',
        'transition-all duration-300 ease-out motion-reduce:transition-none',
        modalPresence.phase === 'entered' ? 'opacity-100' : 'opacity-0',
        (showMetadataPanel || showFilterPanel) && 'md:pl-80'
      )}
      data-testid="modal-backdrop"
      onClick={handleBackdropClick}
    >
      {/* Top button bar */}
      <div
        className={cn(
          'absolute top-2 sm:top-4 z-10 flex gap-1 sm:gap-2 transition-all duration-300',
          'right-2 sm:right-4'
        )}
      >
        {/* Info/Metadata toggle button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => {
            setShowFilterPanel(false);
            setShowMetadataPanel((prev) => !prev);
          }}
          aria-label="Toggle metadata panel"
          title="情報パネル表示切替 (i)"
          data-testid="modal-info-button"
          className={cn(
            'text-white h-10 w-10 sm:h-12 sm:w-12',
            showMetadataPanel ? 'bg-accent/20 text-accent' : 'hover:bg-white/20'
          )}
        >
          <Info className="h-5 w-5 sm:h-6 sm:w-6" />
        </Button>

        {!readOnly && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              setShowMetadataPanel(false);
              setShowFilterPanel((prev) => !prev);
            }}
            aria-label="Toggle filter panel"
            title="画像加工 (f)"
            data-testid="modal-filter-button"
            className={cn(
              'text-white h-10 w-10 sm:h-12 sm:w-12',
              showFilterPanel ? 'bg-accent/20 text-accent' : 'hover:bg-white/20'
            )}
          >
            <SlidersHorizontal className="h-5 w-5 sm:h-6 sm:w-6" />
          </Button>
        )}

        {/* Download button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={handleDownload}
          disabled={isDownloading}
          aria-label={`Download photo (${userPlan ? getDownloadQualityLabel(userPlan.planType) : 'loading...'})`}
          title={`ダウンロード（${userPlan ? getDownloadQualityLabel(userPlan.planType) : ''}）`}
          data-testid="modal-download-button"
          className="text-white hover:bg-success/20 hover:text-success h-10 w-10 sm:h-12 sm:w-12 disabled:opacity-50"
        >
          <Download className="h-5 w-5 sm:h-6 sm:w-6" />
        </Button>

        {/* Share button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={handleShare}
          disabled={createShare.isPending}
          aria-label="Share photo"
          data-testid="modal-share-button"
          className="text-white hover:bg-accent/20 hover:text-accent h-10 w-10 sm:h-12 sm:w-12 disabled:opacity-50"
        >
          <Share2 className="h-5 w-5 sm:h-6 sm:w-6" />
        </Button>

        {/* Delete button */}
        {onDelete && (
          <Button
            variant="ghost"
            size="icon"
            onClick={handleDeleteClick}
            aria-label="Delete photo"
            data-testid="modal-delete-button"
            className="text-white hover:bg-destructive/20 hover:text-destructive h-10 w-10 sm:h-12 sm:w-12"
          >
            <Trash2 className="h-5 w-5 sm:h-6 sm:w-6" />
          </Button>
        )}

        {/* Close button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          aria-label="Close modal"
          className="text-white hover:bg-white/20 h-10 w-10 sm:h-12 sm:w-12"
        >
          <X className="h-5 w-5 sm:h-6 sm:w-6" />
        </Button>
      </div>

      {/* Delete confirmation dialog */}
      {deleteConfirmPresence.isMounted && (
        <div
          role="alertdialog"
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
          data-presence={deleteConfirmPresence.phase}
          className={cn(
            'absolute inset-0 z-20 flex items-center justify-center bg-black/60 p-4',
            'transition-opacity duration-200 ease-out motion-reduce:transition-none',
            deleteConfirmPresence.phase === 'entered' ? 'opacity-100' : 'opacity-0'
          )}
          data-testid="delete-confirm-dialog"
        >
          <div
            className={cn(
              'max-w-sm w-full rounded-lg bg-card p-4 text-center sm:p-6',
              'transition-all duration-200 ease-out motion-reduce:transition-none',
              deleteConfirmPresence.phase === 'entered'
                ? 'translate-y-0 scale-100 opacity-100'
                : 'translate-y-5 scale-95 opacity-0'
            )}
          >
            <p
              id="delete-dialog-title"
              className="text-card-foreground text-base sm:text-lg mb-3 sm:mb-4"
            >
              Delete this photo?
            </p>
            <p
              id="delete-dialog-description"
              className="text-muted-foreground text-xs sm:text-sm mb-4 sm:mb-6"
            >
              This action cannot be undone.
            </p>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center">
              <Button
                variant="outline"
                onClick={handleCancelDelete}
                data-testid="delete-cancel-button"
                className="text-foreground border-border hover:bg-muted w-full sm:w-auto"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleConfirmDelete}
                data-testid="delete-confirm-button"
                className="w-full sm:w-auto"
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Navigation buttons */}
      {photos && onNavigate && (
        <>
          <Button
            variant="ghost"
            size="icon"
            onClick={handlePrevious}
            disabled={!hasPrev}
            aria-label="Previous photo"
            className={cn(
              'absolute top-1/2 -translate-y-1/2 z-10 text-white hover:bg-white/20 disabled:opacity-30 h-12 w-12 sm:h-14 sm:w-14 transition-all duration-300',
              showMetadataPanel || showFilterPanel
                ? 'left-2 sm:left-4 md:left-[336px]'
                : 'left-2 sm:left-4'
            )}
          >
            <ChevronLeft className="h-6 w-6 sm:h-8 sm:w-8" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            onClick={handleNext}
            disabled={!hasNext}
            aria-label="Next photo"
            className={cn(
              'absolute top-1/2 -translate-y-1/2 z-10 text-white hover:bg-white/20 disabled:opacity-30 h-12 w-12 sm:h-14 sm:w-14 transition-all duration-300',
              'right-2 sm:right-4'
            )}
          >
            <ChevronRight className="h-6 w-6 sm:h-8 sm:w-8" />
          </Button>
        </>
      )}

      {/* Modal content */}
      <div
        className={cn(
          'relative z-10 flex flex-col items-center max-w-7xl max-h-screen px-2 py-4 sm:p-8',
          showMetadataPanel || showFilterPanel ? 'justify-start' : 'justify-center'
        )}
        onClick={handleBackdropClick}
        data-testid="modal-content"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Image */}
        {imageError ? (
          <div className="flex items-center justify-center w-48 h-48 sm:w-64 sm:h-64 bg-card rounded-lg">
            <p className="text-white text-center text-sm sm:text-base">Failed to load image</p>
          </div>
        ) : (
          <div className="relative inline-block">
            <img
              id="modal-title"
              key={photo.id}
              src={photo.originalUrl}
              alt={photo.filename}
              loading="lazy"
              onDoubleClick={handleImageDoubleClick}
              onError={() => setImageError(true)}
              className={cn(
                'max-w-full object-contain transition-all duration-300 motion-reduce:transition-none',
                showMetadataPanel ? 'max-h-[55vh] sm:max-h-[80vh]' : 'max-h-[85vh] sm:max-h-[80vh]',
                isZoomed ? 'cursor-zoom-out' : 'cursor-zoom-in',
                isZoomed && 'touch-pan-x touch-pan-y',
                isImageTransitioning ? 'opacity-70' : 'opacity-100'
              )}
              style={{
                transform: `scale(${zoomScale})`,
                transformOrigin: 'center center',
                filter: showFilterPanel ? buildImageCssFilter(filterSettings) : undefined,
              }}
            />
            {showFilterPanel && (
              <>
                {showCropOverlay && (
                  <div
                    data-testid="crop-overlay"
                    className="pointer-events-none absolute inset-6 z-10 rounded-lg border-2 border-white/80 shadow-[0_0_0_9999px_rgba(0,0,0,0.45)]"
                  >
                    <div className="absolute inset-0 grid grid-cols-3 grid-rows-3">
                      {Array.from({ length: 9 }).map((_, index) => (
                        <div key={index} className="border border-white/25" />
                      ))}
                    </div>
                  </div>
                )}
                {filterSettings.vignette > 0 && (
                  <div
                    className="pointer-events-none absolute inset-0"
                    style={{
                      background: `radial-gradient(circle, transparent 40%, rgba(0,0,0,${filterSettings.vignette * 0.85}) 100%)`,
                    }}
                  />
                )}
                {filterSettings.temperature !== 0 && (
                  <div
                    className="pointer-events-none absolute inset-0"
                    style={{
                      backgroundColor:
                        filterSettings.temperature > 0
                          ? `rgba(255,140,0,${filterSettings.temperature * 0.12})`
                          : `rgba(0,100,255,${Math.abs(filterSettings.temperature) * 0.12})`,
                      mixBlendMode: 'multiply',
                    }}
                  />
                )}
                {filterSettings.grain > 0 && (
                  <div
                    className="pointer-events-none absolute inset-0"
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
                      backgroundSize: '150px 150px',
                      opacity: filterSettings.grain * 0.5,
                      mixBlendMode: 'overlay',
                    }}
                  />
                )}
                {filterSettings.fade > 0 && (
                  <div
                    className="pointer-events-none absolute inset-0"
                    style={{
                      backgroundColor: `rgba(180,180,180,${filterSettings.fade * 0.25})`,
                      mixBlendMode: 'lighten',
                    }}
                  />
                )}
              </>
            )}
          </div>
        )}

        {/* Metadata */}
        {showMetadata && (
          <div className="mt-3 sm:mt-4 px-4 py-2 sm:px-6 sm:py-3 rounded-lg bg-black/60 backdrop-blur-sm text-white max-w-full animate-fade-in motion-reduce:animate-none">
            <p className="text-base sm:text-lg font-medium truncate" aria-live="polite">
              {photo.filename}
            </p>
          </div>
        )}
      </div>

      {/* Metadata Panel */}
      {metadataPanelPresence.isMounted && (
        <PhotoMetadataPanel
          photo={photo}
          onPlayerRemoved={onPlayerRemoved}
          onPlayerAdded={onPlayerAdded}
          readOnly={readOnly}
        />
      )}

      {filterPanelPresence.isMounted && (
        <ImageFilterPanel
          previewSrc={photo.originalUrl}
          value={filterSettings}
          isSaving={isSavingFiltered || uploadPhoto.isPending}
          isCropOverlayVisible={showCropOverlay}
          onChange={setFilterSettings}
          onReset={() => setFilterSettings(DEFAULT_IMAGE_FILTER_SETTINGS)}
          onSave={handleSaveFiltered}
          onDownload={handleDownloadFiltered}
          onToggleCropOverlay={() => setShowCropOverlay((prev) => !prev)}
          onClose={() => setShowFilterPanel(false)}
        />
      )}
    </div>
  );
}
